import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useToast
} from "@chakra-ui/react"
import { gql, useMutation } from "@apollo/client"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { setAdminToken } from "../auth/auth.support"

const LOGIN_ADMIN = gql`
  mutation LoginAdmin($username: String!, $password: String!) {
    loginUser(username: $username, password: $password) {
      token
    }
  }
`

const inputStyle = {
  borderRadius: "8px",
  height: "40px",
  border: "1px solid #ddd",
  fontSize: "14px",
  bg: "white",
  _focus: { borderColor: "rgb(163,0,70)", boxShadow: "0 0 0 1px rgb(163,0,70)" }
}

export const LoginPage = (): JSX.Element => {
  const [loginAdmin, { loading }] = useMutation(LOGIN_ADMIN)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const navigate = useNavigate()
  const toast = useToast()

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setErrorMessage("")
    try {
      const { data } = await loginAdmin({ variables: { username: email, password } })
      if (data?.loginUser?.token) {
        setAdminToken(data.loginUser.token, true)
        navigate("/", { replace: true })
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message?.replace("CUSTOM_", "") ?? "Invalid credentials."
      setErrorMessage(msg)
      toast({ title: "Login failed", description: msg, status: "error", duration: 4000, isClosable: true })
    }
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50">
      <Box
        bg="white"
        borderRadius="16px"
        boxShadow="0 4px 24px rgba(0,0,0,0.12)"
        overflow="hidden"
        w="100%"
        maxW="400px"
        mx="4"
      >
        {/* Header */}
        <Box bg="rgb(163,0,70)" px="6" py="6" textAlign="center">
          <Text color="white" fontSize="22px" fontWeight="700" letterSpacing="0.5px">
            SNAP Admin
          </Text>
          <Text color="rgba(255,255,255,0.75)" fontSize="13px" mt="1">
            Fire Systems Management
          </Text>
        </Box>

        {/* Form */}
        <Box px="6" py="6">
          <Text fontSize="16px" fontWeight="600" color="gray.700" mb="5" textAlign="center">
            Sign in to your account
          </Text>
          <form onSubmit={handleSubmit}>
            <Box mb="4">
              <Text fontSize="13px" fontWeight="500" color="gray.700" mb="1">Email address</Text>
              <Input
                {...inputStyle}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
              />
            </Box>

            <Box mb="5">
              <Text fontSize="13px" fontWeight="500" color="gray.700" mb="1">Password</Text>
              <InputGroup>
                <Input
                  {...inputStyle}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  pr="10"
                />
                <InputRightElement h="40px" mr="1">
                  <Button
                    size="xs"
                    variant="unstyled"
                    color="gray.500"
                    fontSize="12px"
                    fontWeight="500"
                    minW="auto"
                    h="auto"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </Box>

            {errorMessage && (
              <Text color="red.500" fontSize="13px" mb="3" textAlign="center">{errorMessage}</Text>
            )}

            <Button
              type="submit"
              w="100%"
              bg="rgb(163,0,70)"
              color="white"
              borderRadius="24px"
              height="42px"
              fontSize="15px"
              fontWeight="600"
              isLoading={loading}
              _hover={{ bg: "rgb(130,0,56)" }}
              _active={{ bg: "rgb(100,0,43)" }}
            >
              Sign In
            </Button>
          </form>
        </Box>
      </Box>
    </Flex>
  )
}
